/**
 * Iter64 — Methodology archive page (renders for /methodology/v<n>).
 *
 * If the requested slug matches the CURRENT methodology version, we
 * navigate to /methodology so the live page stays canonical. Otherwise,
 * we render an archived snapshot driven by the public version registry
 * at GET /api/methodology/version/{slug}.
 *
 * Older versions are intentionally shown as a SNAPSHOT — not a full
 * re-render of the entire methodology — so reviewers can see exactly
 * what was scoped at that version (highlights, bumped sections, status)
 * without us having to maintain N parallel React trees forever.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ScrollText, ExternalLink, Loader2, AlertOctagon, ArrowRight,
  ChevronRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";


import useDocumentTitle from "../hooks/useDocumentTitle";

const BACKEND = process.env.REACT_APP_BACKEND_URL;


export default function MethodologyVersionPage() {
  useDocumentTitle('Methodology Version — QorTrace');
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const r = await axios.get(
          `${BACKEND}/api/methodology/version/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        // If this slug is the current version, redirect to canonical.
        if (r.data?.version?.status === "current") {
          navigate("/methodology", { replace: true });
          return;
        }
        setData(r.data);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-12"
           data-testid="methodology-archive-page">
        {loading && (
          <div className="qt-mono text-sm py-12"
               style={{ color: "var(--qt-text-3)" }}>
            <Loader2 size={13} className="animate-spin inline mr-2" />
            Loading methodology archive…
          </div>
        )}

        {error && (
          <div className="qt-card py-12 text-center"
               data-testid="archive-error"
               style={{ background: "rgba(255,110,110,0.05)",
                        borderColor: "rgba(255,110,110,0.30)" }}>
            <AlertOctagon size={22}
                          style={{ color: "#FF6E6E", margin: "0 auto" }} />
            <p className="qt-mono mt-3"
               style={{ color: "#FF6E6E", letterSpacing: "0.06em" }}>
              {error}
            </p>
            <Link to="/methodology" data-testid="archive-back-current"
                  className="qt-btn qt-btn-secondary inline-flex items-center gap-2 mt-4"
                  style={{ fontSize: 12 }}>
              <ArrowRight size={11} /> View current methodology
            </Link>
          </div>
        )}

        {data && (
          <ArchiveContent version={data.version} allSlugs={data.all_slugs} />
        )}
      </div>
      <Footer />
    </div>
  );
}


function ArchiveContent({ version, allSlugs }) {
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="qt-mono text-[10px] px-2.5 py-1 rounded"
              data-testid="archive-status-pill"
              style={{ color: "var(--qt-text-3)",
                       border: "1px solid var(--qt-border)",
                       background: "rgba(255,255,255,0.02)",
                       letterSpacing: "0.16em", fontWeight: 700 }}>
          ARCHIVED · {version.status.toUpperCase()}
        </span>
        <span className="qt-mono text-[11px]"
              style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}>
          PUBLISHED {version.date}
        </span>
      </div>

      <h1 className="qt-h2 text-3xl sm:text-4xl"
          data-testid="archive-title">
        {version.id}
      </h1>
      <p className="qt-mono text-sm mt-2"
         style={{ color: "var(--qt-cyan)", letterSpacing: "0.04em" }}
         data-testid="archive-subtitle">
        {version.title}
      </p>

      <div className="qt-card mt-6"
           style={{ padding: "18px 22px",
                    background: "rgba(255,176,0,0.06)",
                    borderColor: "rgba(255,176,0,0.30)",
                    borderLeft: "3px solid var(--qt-amber)" }}>
        <div className="qt-label mb-1.5"
             style={{ color: "var(--qt-amber)",
                      letterSpacing: "0.16em" }}>
          ARCHIVED VERSION
        </div>
        <p style={{ color: "var(--qt-text-1)",
                    fontSize: 14, lineHeight: 1.65 }}>
          This page is a frozen snapshot of QorTrace methodology{" "}
          <strong style={{ color: "#fff" }}>{version.id}</strong>. Reports
          stamped at this version remain reproducible against these rules.
          For the current methodology, see{" "}
          <Link to="/methodology"
                data-testid="archive-cta-current"
                style={{ color: "var(--qt-cyan)",
                         textDecoration: "underline" }}>
            /methodology
          </Link>.
        </p>
      </div>

      <section className="mt-10" data-testid="archive-summary">
        <div className="qt-label mb-3"
             style={{ letterSpacing: "0.18em" }}>SUMMARY</div>
        <p style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
          {version.summary}
        </p>
      </section>

      <section className="mt-10" data-testid="archive-highlights">
        <div className="qt-label mb-3"
             style={{ letterSpacing: "0.18em" }}>WHAT SHIPPED IN THIS VERSION</div>
        <ul className="space-y-2">
          {(version.highlights || []).map((h, i) => (
            <li key={i}
                className="qt-card flex items-start gap-3"
                style={{ padding: "12px 16px",
                         background: "rgba(4,9,20,0.55)",
                         borderColor: "var(--qt-border-soft)" }}>
              <ChevronRight size={13}
                             style={{ color: "var(--qt-cyan)",
                                      flexShrink: 0, marginTop: 3 }} />
              <span className="text-[13px]"
                    style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
                {h}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10" data-testid="archive-other-versions">
        <div className="qt-label mb-3"
             style={{ letterSpacing: "0.18em" }}>OTHER VERSIONS</div>
        <div className="flex flex-wrap gap-2">
          <Link to="/methodology"
                className="qt-btn qt-btn-secondary inline-flex items-center gap-2"
                data-testid="archive-link-current"
                style={{ fontSize: 12 }}>
            <ScrollText size={11} /> Current
          </Link>
          {(allSlugs || []).filter((s) => s !== version.slug).map((s) => (
            <Link key={s} to={`/methodology/${s}`}
                  data-testid={`archive-link-${s}`}
                  className="qt-btn qt-btn-ghost inline-flex items-center gap-2"
                  style={{ fontSize: 12 }}>
              {s.toUpperCase()}
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-10" data-testid="archive-rss">
        <a href={`${BACKEND}/api/methodology.rss`}
           target="_blank" rel="noopener noreferrer"
           className="qt-mono text-xs inline-flex items-center gap-1.5"
           style={{ color: "var(--qt-cyan)",
                    letterSpacing: "0.06em",
                    textDecoration: "underline",
                    textUnderlineOffset: 3 }}>
          Subscribe to methodology updates · RSS
          <ExternalLink size={10} />
        </a>
      </section>
    </>
  );
}
